import albsl from '@/assets/logos/albsl.png'
import api from '@/assets/logos/api.png'
import avyan from '@/assets/logos/avyan.png'
import bhl from '@/assets/logos/bhl.png'
import cfcl from '@/assets/logos/cfcl.png'
import cit from '@/assets/logos/cit.png'
import gbime from '@/assets/logos/gbime.png'
import gufl from '@/assets/logos/gufl.png'
import hdl from '@/assets/logos/hdl.png'
import mepdl from '@/assets/logos/mepdl.png'
import mlbl from '@/assets/logos/mlbl.png'
import nabil from '@/assets/logos/nabil.png'
import nica from '@/assets/logos/nica.png'
import nric from '@/assets/logos/nric.png'
import ntc from '@/assets/logos/ntc.png'
import sapil from '@/assets/logos/sapil.png'
import shivm from '@/assets/logos/shivm.png'
import upper from '@/assets/logos/upper.png'
import { config } from '@/lib/config'

/**
 * Company marks, keyed by NEPSE symbol, bundled with the app.
 *
 * The last image `StockAvatar` tries, after the CDN logo (`companyLogoUrl`)
 * and the row's own `logo_url`: the marks the Home and Market frames use, so
 * a CDN outage still leaves those cards with their logos rather than a grey
 * circle. The map is deliberately partial: a symbol with no entry falls back
 * to its initial rather than to a broken image.
 */
export const SYMBOL_LOGOS: Record<string, string> = {
  ALBSL: albsl,
  API: api,
  AVYAN: avyan,
  BHL: bhl,
  CFCL: cfcl,
  CIT: cit,
  GBIME: gbime,
  GUFL: gufl,
  HDL: hdl,
  MEPDL: mepdl,
  MLBL: mlbl,
  NABIL: nabil,
  NICA: nica,
  NRIC: nric,
  NTC: ntc,
  SAPIL: sapil,
  SHIVM: shivm,
  UPPER: upper,
}

/**
 * A company's logo on the CDN, from its NEPSE symbol alone —
 * `{VITE_COMPANY_LOGO_BASE_URL}{SYMBOL}.png`.
 *
 * Built here, not read off the API, by the 2026-09-28 ruling that replaced
 * "no web-side URL rewriting": `logo_url` then 404'd for every symbol, and most
 * endpoints (movers, holdings, floorsheet, alerts, watchlist stocks) send no
 * logo at all. Since the prod data reached dev (2026-11-01) `logo_url` serves
 * 271 of 279 symbols, so `StockAvatar` now tries it, and the directory below,
 * whenever this URL misses. The symbol is upper-cased because the CDN's file
 * names are, and encoded so an odd one can never reach outside the base path.
 */
export const companyLogoUrl = (symbol: string): string =>
  `${config.companyLogoBaseUrl}${encodeURIComponent(symbol.trim().toUpperCase())}.png`

/**
 * The same symbol's file under the CDN's `/logo_full/` folder, a sibling of the
 * `/media/` base. On the prod data (measured 2026-11-01, `/market/live/`'s 279
 * rows) `/media/{SYM}.png` serves 221 symbols and `/logo_full/{SYM}.png` 234,
 * and 37 symbols have a file in only one of the two, so each covers the other's
 * gaps.
 */
export const companyFullLogoUrl = (symbol: string): string =>
  new URL(
    `../logo_full/${encodeURIComponent(symbol.trim().toUpperCase())}.png`,
    config.companyLogoBaseUrl,
  ).href

/**
 * Every listed company's own `logo_url`, keyed by symbol, from `/market/live/`
 * (`prefetchCompanyLogos`). Only `Stock`, `CompanyProfile` and `FundRow` carry
 * a logo; movers, holdings, floorsheet, alerts and the other 30-odd row shapes
 * send a bare symbol. Read through this directory, a row with no logo of its
 * own still lands on the file mm-be names, including the ones whose name is
 * not `{SYMBOL}.png` (`Tansen_logo.png`, `SARVOTTAM.jpg`). Module-level, like
 * `StockAvatar`'s probe state: one directory for the whole app, filled once.
 */
const logoDirectory = new Map<string, string>()
const logoDirectoryListeners = new Set<() => void>()

/** Adds each row's absolute `logo_url` under its symbol, and wakes every avatar reading it. */
export const registerCompanyLogos = (
  rows: Iterable<{ symbol: string; logoUrl: string | null }>,
): void => {
  for (const { symbol, logoUrl } of rows) {
    if (logoUrl !== null && /^https?:\/\//i.test(logoUrl)) {
      logoDirectory.set(symbol.trim().toUpperCase(), logoUrl)
    }
  }
  for (const listener of logoDirectoryListeners) listener()
}

export const subscribeCompanyLogos = (onChange: () => void): (() => void) => {
  logoDirectoryListeners.add(onChange)
  return () => {
    logoDirectoryListeners.delete(onChange)
  }
}

export const directoryLogoUrl = (symbol: string): string | undefined =>
  logoDirectory.get(symbol.trim().toUpperCase())

/**
 * A backend media path (`broker/logo/….png`) made absolute against the same
 * base; an absolute URL passes through untouched.
 *
 * The broker header's `logo_url` is such a path: the backend stores the file
 * relative to its media root and never prefixes it, and the CDN serves it
 * under the same `/media/` base as the company logos (measured 2026-09-28:
 * `/media/broker/logo/<hash>.png` is a 200 `image/png`). A leading slash is
 * dropped so the path lands under the base rather than at the host's root.
 */
export const companyMediaUrl = (path: string): string =>
  /^https?:\/\//i.test(path)
    ? path
    : new URL(path.replace(/^\/+/, ''), config.companyLogoBaseUrl).href
